<template>
  <ValidationObserver ref="form" v-slot="{ handleSubmit, invalid }" slim>
    <form class="auth-form" @submit.prevent="handleSubmit(handleLogin)">
      <!--switch signup-->
      <div
        v-if="['B', 'C'].includes(layout) && menu === 'button'"
        class="text-center mb-4"
      >
        <span class="me-2">{{ $t('component.loginModal.signUp') }}</span>
        <a
          href="#"
          class="text-decoration-underline auth-form-link"
          @click.prevent="$emit('switchCategory', 'signUp')"
        >
          {{ $t('component.AuthModal.list.signUp') }}</a
        >
      </div>

      <div class="mb-4">
        <label class="form-label required" for="username">
          {{ $t('common.profile.username') }}
        </label>
        <ValidationProvider
          ref="usernameRef"
          v-slot="{ valid, invalid, dirty }"
          vid="username"
          :name="$t('common.profile.username')"
          rules="required"
          slim
        >
          <input
            id="username"
            v-model="username"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            :placeholder="$t('component.loginModal.username.placeholder')"
            name="username"
            type="text"
            class="form-control"
            autofocus
          />
        </ValidationProvider>
      </div>

      <div class="mb-4">
        <div class="mb-2 d-flex justify-content-between">
          <label class="mb-0 form-label required" for="password">
            {{ $t('common.profile.password') }}
          </label>
          <a
            href="#"
            class="text-decoration-underline auth-form-link"
            @click.prevent="$emit('switchCategory', 'forgetPwd')"
            >{{ $t('component.loginModal.forgotPassword') }}</a
          >
        </div>
        <ValidationProvider
          ref="passwordRef"
          v-slot="{ valid, invalid, dirty }"
          vid="password"
          :name="$t('common.profile.password')"
          rules="required|min:6|max:16"
          slim
        >
          <input
            id="password"
            v-model="password"
            :class="{
              'is-valid': valid,
              'is-invalid': dirty && invalid
            }"
            :placeholder="$t('component.loginModal.password.placeholder')"
            name="password"
            type="password"
            class="form-control"
          />
        </ValidationProvider>
      </div>

      <div class="mb-4">
        <label class="form-label required" for="imageCode">
          {{ $t('common.imageCode') }}
        </label>
        <div class="d-flex gap-3 align-items-center">
          <ValidationProvider
            ref="imageCodeRef"
            v-slot="{ valid, invalid, dirty }"
            vid="imageCode"
            :name="$t('common.imageCode')"
            rules="required"
            slim
          >
            <input
              id="imageCode"
              v-model="imageCode"
              :class="{
                'is-valid': valid,
                'is-invalid': dirty && invalid
              }"
              :placeholder="$t('component.loginModal.imageCode.placeholder')"
              name="imageCode"
              type="text"
              class="form-control"
              autocomplete="off"
            />
          </ValidationProvider>
          <div class="verify-code-pic">
            <img
              v-if="imageSrc !== null"
              :src="'data:image/jpeg;base64, ' + imageSrc"
              alt="image-code"
            />
            <i v-if="imageSrc === null" class="km-icon-spinner" />
          </div>
          <i
            class="km-icon-arrow-rotate auth-form-link verify-code-update-icon"
            @click.prevent="refreshImageSrc"
          />
        </div>
      </div>

      <div class="mb-4">
        <button
          :disabled="invalid"
          type="submit"
          class="w-100 btn rounded-pill"
          :class="customBtnPrimaryClass"
        >
          {{ $t('common.submit') }}
        </button>
      </div>

      <!--switch signup-->
      <div v-if="layout === 'A' && menu === 'button'" class="text-center">
        <span class="me-2">{{ $t('component.loginModal.signUp') }}</span>
        <a
          href="#"
          class="text-decoration-underline auth-form-link"
          @click.prevent="$emit('switchCategory', 'signUp')"
        >
          {{ $t('component.AuthModal.list.signUp') }}</a
        >
      </div>

      <!--oauth-->
      <div
        v-if="hasTelegramOauth || hasGoogleOauth || hasLineOauth"
        class="divider my-4"
      >
        or
      </div>
      <div
        v-if="hasTelegramOauth || hasGoogleOauth || hasLineOauth"
        class="d-grid gap-3"
      >
        <button
          v-if="hasTelegramOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="loginWithTelegram"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_telegram.png`
            "
            alt="btn-oauth-telegram"
          />
          <span v-html="$t('common.oauth.login', ['Telegram'])"></span>
        </button>
        <button
          v-if="hasGoogleOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="loginWithGoogle"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_google.png`
            "
            alt="btn-oauth-google"
          />
          <span v-html="$t('common.oauth.login', ['Google'])"></span>
        </button>
        <button
          v-if="hasLineOauth"
          class="btn btn-outline-gray btn-oauth rounded-pill"
          @click.prevent="loginWithLine"
        >
          <img
            :src="
              `https://ik.imagekit.io/1pzxddx3qqv/common/img/btn_oauth_line.png`
            "
            alt="btn-oauth-line"
          />
          <span v-html="$t('common.oauth.login', ['Line'])"></span>
        </button>
      </div>
    </form>
  </ValidationObserver>
</template>

<script>
import {
  computed,
  defineComponent,
  onMounted,
  useStore
} from '@nuxtjs/composition-api'
import { catchError, concatMap, from, tap } from 'rxjs'
import useVeeValidate from '../composables/useVeeValidate'
import useErrorHandler from '../composables/useErrorHandler'
import useComp from '../composables/useComp'
import useOauth from '../composables/useOauth'

export default defineComponent({
  props: {
    layout: {
      type: String,
      default: 'A'
    },
    menu: {
      type: String,
      default: 'tabs'
    },
    // custom class
    customBtnPrimaryClass: {
      type: String,
      default: 'btn-primary'
    }
  },
  setup(props, context) {
    const store = useStore()

    const { isDev } = useComp()

    const version = computed(() => store.state.version)
    const username = computed({
      get() {
        return store.state.authModule.username
      },
      set(value) {
        store.commit('authModule/USERNAME', value)
      }
    })
    const password = computed({
      get() {
        return store.state.authModule.password
      },
      set(value) {
        store.commit('authModule/PASSWORD', value)
      }
    })
    const imageSrc = computed({
      get() {
        return store.state.authModule.imageSrc
      },
      set(value) {
        store.commit('authModule/IMAGE_SRC', value)
      }
    })
    const imageCode = computed({
      get() {
        return store.state.authModule.imageCode
      },
      set(value) {
        store.commit('authModule/IMAGE_CODE', value)
      }
    })
    const imageToken = computed({
      get() {
        return store.state.authModule.imageToken
      },
      set(value) {
        store.commit('authModule/IMAGE_TOKEN', value)
      }
    })

    const { reset } = useVeeValidate(context)
    const { errorHandler, errorHandler$ } = useErrorHandler(context)

    onMounted(() => {
      refreshData()
    })

    function refreshData() {
      username.value = null
      password.value = null
      refreshImageSrc()
      reset()
    }

    function refreshImageSrc() {
      imageSrc.value = null
      imageCode.value = null
      imageToken.value = null
      store.dispatch('authModule/getImageSrc').catch(err => errorHandler(err))
      reset('imageCodeRef')
    }

    function handleLogin() {
      login(loginSuccess)
    }

    function login(successCallback) {
      from(store.dispatch('startLoading'))
        .pipe(
          concatMap(() => from(store.dispatch('authModule/login'))),
          tap(() => {
            successCallback()
          }),
          catchError(err => {
            refreshImageSrc()
            return errorHandler$(err)
          })
        )
        .subscribe({
          complete: () => store.dispatch('completeLoading')
        })
    }

    function loginSuccess() {
      context.emit('switchCategory', null)

      context.emit('loginSuccessAfter')
    }

    const { signInWithTelegram, signInWithGoogle, signInWithLine } = useOauth(
      context
    )
    const hasTelegramOauth = computed(() => !!process.env.OAUTH_TELEGRAM_BOT_ID)
    function loginWithTelegram() {
      signInWithTelegram(loginSuccess, () => {
        refreshImageSrc()
      })
    }
    const hasGoogleOauth = computed(() => !!process.env.OAUTH_GOOGLE_CLIENT_ID)
    function loginWithGoogle() {
      signInWithGoogle(loginSuccess, () => {
        refreshImageSrc()
      })
    }
    const hasLineOauth = computed(
      () => !!process.env.OAUTH_LINE_ID && !!process.env.OAUTH_LINE_SECRET
    )
    function loginWithLine() {
      signInWithLine(loginSuccess, () => {
        refreshImageSrc()
      })
    }

    return {
      isDev,
      version,
      username,
      password,
      imageSrc,
      imageCode,
      imageToken,

      refreshImageSrc,
      handleLogin,

      hasTelegramOauth,
      loginWithTelegram,
      hasGoogleOauth,
      loginWithGoogle,
      hasLineOauth,
      loginWithLine
    }
  }
})
</script>
